import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { pickLocaleName, pickLocaleString, useLocaleStore, type Locale } from '@/i18n';
import type { HomeGameItemDto } from '@/types/home';
import type {
  GameDetailsDto,
  GameFavoritesDto,
  GameListResponseDto,
  GamePageDto,
  GameProviderDto,
  GameRecommendDto,
  GameTabId,
} from '@/types/game';
import { apiGet } from './_base';
import {
  type FavoriteGamesResponse,
  type GameDto,
  type GameListResponse,
  useGameCatalogQuery,
  useGameInfoQuery,
} from './casino';
import { pickLocaleAsset } from './uiConfig';

// UI tabs → casino_games.system_type values (exposed as GameDto.type on the
// list response). Filtering by type instead of game_aggregator means each tab
// pulls every provider whose games belong to that category, not just the one
// vendor the aggregator used to pin (which is why e.g. the Slots tab was
// missing every non-PGSOFT slots provider). `sports` stays null — no backend
// type wired yet.
const TAB_TYPE: Record<GameTabId, string | null> = {
  live: 'live',
  slots: 'slots',
  hash: 'ORIGINAL_GAMES',
  card: 'card',
  fishing: 'fishing',
  sports: 'sports',
  lottery: 'lottery',
};

// Canonical provider labels used everywhere the UI shows a provider name.
// Backend provider constants stay stable (`PGSOFT`, `DBLIVE`, etc.); the UI
// resolves them through this table so Chinese users see the agreed frontend
// equivalents while other locales keep their locale-specific labels.
const PROVIDER_TITLE: Record<string, Record<Locale, string>> = {
  // Live Casino
  DBLIVE:          { en: 'DB Live',         zh: 'DB真人',          'zh-tw': 'DB真人',          ja: 'DB ライブ',          ko: 'DB 라이브',         vi: 'DB Live'         },
  TGLive:          { en: 'TG Live',         zh: 'TG真人',          'zh-tw': 'TG真人',          ja: 'TG ライブ',          ko: 'TG 라이브',         vi: 'TG Live'         },
  SolaireLive:     { en: 'Solaire Live',    zh: '索莱尔视讯',      'zh-tw': '索萊爾視訊',      ja: 'Solaire ライブ',     ko: 'Solaire 라이브',    vi: 'Solaire Live'    },
  // Slots
  PGSOFT:          { en: 'PG Soft',         zh: 'PG电子',          'zh-tw': 'PG電子',          ja: 'PG Soft',            ko: 'PG Soft',           vi: 'PG Soft'         },
  PragmaticPlay:   { en: 'Pragmatic Play',  zh: 'PP电子',          'zh-tw': 'PP電子',          ja: 'Pragmatic Play',     ko: 'Pragmatic Play',    vi: 'Pragmatic Play'  },
  pplive:          { en: 'Pragmatic Play',  zh: 'PP电子',          'zh-tw': 'PP電子',          ja: 'Pragmatic Play',     ko: 'Pragmatic Play',    vi: 'Pragmatic Play'  },
  // Hash — backend uses '99H.COM' on some endpoints and '99H.COM' on others;
  // both resolve to the same display name.
  'HG.GAME':       { en: 'HG Original',     zh: 'HG 原创',         'zh-tw': 'HG 原創',         ja: 'HG オリジナル',      ko: 'HG 오리지널',       vi: 'HG Original'     },
  '99H.COM':       { en: 'HG Original',     zh: 'HG 原创',         'zh-tw': 'HG 原創',         ja: 'HG オリジナル',      ko: 'HG 오리지널',       vi: 'HG Original'     },
  // Sports — backend uses both 'CRSports' and 'CR Sports' (space variant)
  H99SPORT:        { en: '99H Sport',       zh: '99H体育',         'zh-tw': '99H體育',         ja: '99H スポーツ',       ko: '99H 스포츠',        vi: '99H Thể thao'    },
  '99HSPORT':      { en: '99H Sport',       zh: '99H体育',         'zh-tw': '99H體育',         ja: '99H スポーツ',       ko: '99H 스포츠',        vi: '99H Thể thao'    },
  CRSports:        { en: 'CR Sports',       zh: '皇冠体育',        'zh-tw': '皇冠體育',        ja: 'CR スポーツ',        ko: 'CR 스포츠',         vi: 'CR Thể thao'     },
  'CR Sports':     { en: 'CR Sports',       zh: '皇冠体育',        'zh-tw': '皇冠體育',        ja: 'CR スポーツ',        ko: 'CR 스포츠',         vi: 'CR Thể thao'     },
  SABASports:      { en: 'SABA Sports',     zh: '沙巴体育',        'zh-tw': '沙巴體育',        ja: 'SABA スポーツ',      ko: 'SABA 스포츠',       vi: 'SABA Thể thao'   },
  // Card / Fishing
  DBCARD:          { en: 'DB Card',         zh: 'DB棋牌',          'zh-tw': 'DB棋牌',          ja: 'DB カード',          ko: 'DB 카드',           vi: 'DB Bài'          },
  DBFISHING:       { en: 'DB Fishing',      zh: 'DB捕鱼',          'zh-tw': 'DB捕魚',          ja: 'DB フィッシング',    ko: 'DB 피싱',           vi: 'DB Bắn cá'       },
  // Lottery — without an entry the heading fell through to the raw backend
  // constant and read "CANADA28" in every locale.
  CANADA28:        { en: 'Original Lottery', zh: '原创彩票',        'zh-tw': '原創彩票',        ja: 'オリジナル宝くじ',   ko: '오리지널 복권',      vi: 'Original Lottery' },
};

function normalizeProviderKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

const PROVIDER_TITLE_NORMALIZED: Record<string, Record<Locale, string>> = Object.fromEntries(
  Object.entries(PROVIDER_TITLE).map(([key, value]) => [normalizeProviderKey(key), value]),
);

const GAME_NAME_PROVIDER_FALLBACK = new Set([
  normalizeProviderKey('SABASports'),
  normalizeProviderKey('CRSports'),
  normalizeProviderKey('CR Sports'),
]);

export function providerTitle(name: string, locale: Locale): string {
  const parsed = pickLocaleString(name, locale);
  if (parsed !== null) return parsed;
  const entry = PROVIDER_TITLE_NORMALIZED[normalizeProviderKey(name)];
  if (!entry) return name;
  return entry[locale] ?? entry.en ?? name;
}

/**
 * Backend normally provides both `game_name` and `game_name_zh`. Two sports
 * provider-backed names are the exception, so when the zh column is empty we
 * fall back to the frontend provider mapping for those ids only.
 */
export function gameTitle(name: string | undefined, nameZh: string | undefined, locale: Locale): string {
  const title = pickLocaleName(name, nameZh, locale);
  const isChineseLocale = locale === 'zh' || locale === 'zh-tw';
  if (!isChineseLocale || nameZh || !name) return title;
  if (!GAME_NAME_PROVIDER_FALLBACK.has(normalizeProviderKey(name))) return title;
  return providerTitle(name, locale);
}

const TAB_LABELS: Record<GameTabId, Record<Locale, string>> = {
  live:    { en: 'Live Casino', zh: '真人娱乐', 'zh-tw': '真人娛樂', ja: 'ライブカジノ',   ko: '라이브 카지노', vi: 'Live Casino' },
  slots:   { en: 'Slots',       zh: '电子',     'zh-tw': '電子',     ja: 'スロット',       ko: '슬롯',          vi: 'Slot'        },
  hash:    { en: 'Hash Game',   zh: '哈希',     'zh-tw': '哈希',     ja: 'ハッシュゲーム', ko: '해시 게임',     vi: 'Hash Game'   },
  sports:  { en: 'Sports',      zh: '体育',     'zh-tw': '體育',     ja: 'スポーツ',       ko: '스포츠',        vi: 'Thể thao'    },
  card:    { en: 'Card',        zh: '棋牌',     'zh-tw': '棋牌',     ja: 'カードゲーム',   ko: '카드 게임',     vi: 'Bài'         },
  fishing: { en: 'Fishing',     zh: '捕鱼',     'zh-tw': '捕魚',     ja: 'フィッシング',   ko: '피싱',          vi: 'Bắn cá'      },
  lottery: { en: 'Lottery',     zh: '彩票',     'zh-tw': '彩票',     ja: '宝くじ',         ko: '복권',          vi: 'Xổ số'       },
};

const TAB_ORDER: GameTabId[] = ['live', 'lottery', 'slots', 'sports', 'fishing', 'card'];
const PREVIEW_LIMIT = 8;
const PAGE_SIZE = 18;

// Tabs are derived from static config, not the catalog response, so the Game
// page chrome can render before the heavy /casino/game/list request resolves.
export function useGameTabs() {
  const locale = useLocaleStore((s) => s.locale);
  return TAB_ORDER.map((id) => ({
    id,
    label: TAB_LABELS[id][locale] ?? TAB_LABELS[id].en,
  }));
}

// Pick the right cover for any catalog game in the active locale. Prefer
// backend-provided system_image/image for every provider. Games with no
// backend cover (many DBCARD/DBLIVE/DBFISHING entries) get an empty string —
// the Image component renders its own in-app placeholder rather than us
// bundling and maintaining local mirrored cover art.
export function coverFor(g: GameDto, locale: Locale): string {
  return pickLocaleAsset(g.system_image || g.image || '', locale);
}

function gameToItem(g: GameDto, locale: Locale): HomeGameItemDto {
  return { id: g.uuid, title: pickLocaleName(g.name, g.name_zh, locale), cover: coverFor(g, locale), maintenance: g.status === 'MAINTENANCE' };
}

// Builds the GamePageDto (tabs + per-tab provider sections) from the real
// /casino/game/list response. Games are bucketed by GameDto.type per tab,
// then grouped by `provider` so multiple vendors under the same category
// render as separate sections.
export function useGameQuery() {
  const locale = useLocaleStore((s) => s.locale);
  const catalog = useGameCatalogQuery();

  const data: GamePageDto | undefined = catalog.data
    ? (() => {
        const games = catalog.data.list_games;
        return {
        tabs: TAB_ORDER.map((id) => ({ id, label: TAB_LABELS[id][locale] ?? TAB_LABELS[id].en })),
        sectionsByTab: TAB_ORDER.reduce<Record<GameTabId, GameProviderDto[]>>((acc, tab) => {
          const type = TAB_TYPE[tab];
          if (type === null) {
            acc[tab] = [];
            return acc;
          }
          const matched = games.filter((g) => (g.type ?? '') === type);
          // Group by provider so each vendor under the aggregator gets its own
          // section. Don't drop tiles without a cover image — DBFISHING and
          // many DBCARD games have no `image`/`system_image`. The Image
          // component renders a placeholder for empty src.
          const byProvider = new Map<string, GameDto[]>();
          for (const g of matched) {
            const key = g.provider ?? '';
            const bucket = byProvider.get(key);
            if (bucket) bucket.push(g);
            else byProvider.set(key, [g]);
          }
          acc[tab] = Array.from(byProvider.entries()).map(([provider, games]) => ({
            id: provider,
            title: providerTitle(provider, locale),
            // Image-first within each provider — DBLIVE in particular has many
            // cover-less games, so prioritising the few that do carry artwork
            // keeps the preview tiles populated rather than mostly placeholders.
            items: games
              .map((g) => gameToItem(g, locale))
              .sort((a, b) => Number(Boolean(b.cover)) - Number(Boolean(a.cover)))
              .slice(0, PREVIEW_LIMIT),
          }));
          return acc;
        }, {} as Record<GameTabId, GameProviderDto[]>),
      };
      })()
    : undefined;

  return { data, isLoading: catalog.isLoading, error: catalog.error };
}

/**
 * Providers for a single category, derived from the same catalog request
 * (`/api/v1/casino/game/list`) that backs the rest of the Game page. Filters
 * the response by the tab's GameDto.type, groups what remains by `provider`,
 * and returns the unique providers with a per-category game count so the
 * picker can sort by popularity within this tab.
 *
 * Reads from cache when warm — no extra API round-trip. Returns an empty
 * list for tabs without a backend type (e.g. sports).
 */
export type CategoryProvider = { name: string; title: string; count: number };

export function useCategoryProvidersQuery(tabId: GameTabId) {
  const locale = useLocaleStore((s) => s.locale);
  const catalog = useGameCatalogQuery();
  const type = TAB_TYPE[tabId];
  const data: CategoryProvider[] | undefined = catalog.data
    ? (() => {
        if (type === null) return [];
        const matched = catalog.data.list_games.filter(
          (g) => (g.type ?? '') === type,
        );
        const byProvider = new Map<string, number>();
        for (const g of matched) {
          const key = g.provider ?? '';
          if (!key) continue;
          byProvider.set(key, (byProvider.get(key) ?? 0) + 1);
        }
        return Array.from(byProvider.entries())
          .sort(([, a], [, b]) => b - a)
          .map(([name, count]) => ({
            name,
            title: providerTitle(name, locale),
            count,
          }));
      })()
    : undefined;
  return { data, isLoading: catalog.isLoading, error: catalog.error };
}

// Per-provider paginated list. We slice from the already-fetched catalog
// (page_size=300 covers every current provider) instead of re-paginating the
// API — that lets us apply a global image-first ordering across all pages
// rather than only sorting within each server page.
export function useGameListQuery(tab: GameTabId, provider: string, pageSize = PAGE_SIZE) {
  const locale = useLocaleStore((s) => s.locale);
  // Pass the provider name to the catalog so the API call includes
  // ?provider_name=<provider> — this ensures the filter is sent server-side
  // on every load, including hard refreshes on a provider-specific URL.
  const catalog = useGameCatalogQuery(1000, provider || undefined);
  const type = TAB_TYPE[tab];
  const games = catalog.data?.list_games ?? [];
  const known = games.some((g) => g.provider === provider);
  const fallback = type !== null
    ? games.find((g) => (g.type ?? '') === type)?.provider ?? ''
    : '';
  const realProvider = known ? provider : fallback;

  // All items for this provider, ordered image-first. Cover-less games stay
  // in the list — the Image component renders a placeholder for empty src.
  const allItems = games
    .filter((g) => g.provider === realProvider)
    .map((g) => gameToItem(g, locale))
    .sort((a, b) => Number(Boolean(b.cover)) - Number(Boolean(a.cover)));

  return useInfiniteQuery({
    queryKey: ['game-list', tab, realProvider, pageSize, locale, allItems.length],
    enabled: Boolean(realProvider) && Boolean(catalog.data),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const start = (pageParam - 1) * pageSize;
      const slice = allItems.slice(start, start + pageSize);
      const dto: GameListResponseDto = {
        tabId: tab,
        providerId: realProvider,
        providerTitle: providerTitle(realProvider, locale),
        page: pageParam,
        pageSize,
        items: slice,
        hasMore: start + pageSize < allItems.length,
      };
      return Promise.resolve(dto);
    },
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  });
}

// Details composed from real catalog: per-game info (name/cover/provider) +
// catalog's is_recommended games for the "Recommend" rail. The legacy
// playUrl field is left empty — the Play page calls /casino/game/load itself
// rather than reading it from here.
export function useGameDetailsQuery(id: string) {
  const locale = useLocaleStore((s) => s.locale);
  const game = useGameInfoQuery(id);
  const catalog = useGameCatalogQuery();

  const data: GameDetailsDto | undefined = game.data
    ? (() => {
        const currentUuid = game.data.uuid;
        return {
          id: game.data.uuid,
          title: pickLocaleName(game.data.name, game.data.name_zh, locale),
          provider: providerTitle(game.data.provider ?? '', locale),
          cover: coverFor(game.data, locale),
          maintenance: game.data.status === 'MAINTENANCE',
          playUrl: '',
          recommend: (catalog.data?.list_games ?? [])
            .filter((g) => g.is_recommended && g.uuid !== currentUuid)
            .map((g) => gameToItem(g, locale))
            .slice(0, 8),
        };
      })()
    : undefined;

  return {
    data,
    isLoading: game.isLoading,
    error: game.error,
  };
}

// Recommend — backed by /api/v1/casino/game/list?is_recommended=true.
// Same endpoint home.ts uses for its Recommend section, but the dedicated
// /game/recommend page asks for a larger slice.
const RECOMMEND_PAGE_SIZE = 60;

export function useGameRecommendQuery() {
  const locale = useLocaleStore((s) => s.locale);
  return useQuery({
    queryKey: ['casino', 'games', 'recommend', 'page', RECOMMEND_PAGE_SIZE, locale],
    queryFn: async (): Promise<GameRecommendDto> => {
      const res = await apiGet<GameListResponse>(
        `/api/v1/casino/game/list?is_recommended=true&page=1&page_size=${RECOMMEND_PAGE_SIZE}`,
      );
      return { items: (res.list_games ?? []).map((g) => gameToItem(g, locale)) };
    },
  });
}

// Favorites — backed by /api/v1/casino/game/fav for the user's marked list,
// with a second /casino/game/list?is_recommended=true call powering the
// horizontal "Recommend" strip at the bottom of the page.
const FAVORITES_RECOMMEND_SIZE = 20;

export function useGameFavoritesQuery() {
  const locale = useLocaleStore((s) => s.locale);
  const fav = useQuery({
    queryKey: ['casino', 'game-fav', locale],
    queryFn: () => apiGet<FavoriteGamesResponse>('/api/v1/casino/game/fav'),
  });
  const rec = useQuery({
    queryKey: [
      'casino',
      'games',
      'recommend',
      'page',
      FAVORITES_RECOMMEND_SIZE,
      locale,
    ],
    queryFn: () =>
      apiGet<GameListResponse>(
        `/api/v1/casino/game/list?is_recommended=true&page=1&page_size=${FAVORITES_RECOMMEND_SIZE}`,
      ),
  });

  const data: GameFavoritesDto | undefined =
    fav.data && rec.data
      ? {
          items: (fav.data.games ?? []).map((g) => gameToItem(g, locale)),
          recommend: (rec.data.list_games ?? []).map((g) => gameToItem(g, locale)),
        }
      : undefined;

  return {
    data,
    isLoading: fav.isLoading || rec.isLoading,
    error: fav.error ?? rec.error,
  };
}
